// src/services/albumPhotoServices.js
import axios from "axios";

// Compat Vite & CRA
const _base =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  "";

const baseUrl = _base.replace(/\/$/, "");

// Endpoints back (upload local sharp)
const galleriesBase = `${baseUrl}/api/galleries`;
const photosBase = `${baseUrl}/api/photos`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Crée une photo dans une gallerie ET téléverse le fichier en une requête.
 * - Route: POST /api/galleries/:gallerie_id/photos
 * - Champ fichier: "image"
 * - Query: ?format=webp|jpg|png (def: webp)
 * - Retour: { id, gallerie_id, photo_url, description, is_active }
 *
 * @param {number|string} gallerieId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {string} [options.description=""]
 * @param {boolean} [options.is_active=true]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<object>}
 */
export const addAlbumPhoto = async (gallerieId, file, options = {}) => {
  if (!gallerieId) throw new Error("gallerieId requis");
  if (!file) throw new Error("file requis");

  const {
    format = "webp",
    description = "",
    is_active = true,
    onUploadProgress,
  } = options;

  const form = new FormData();
  form.append("image", file); // ⚠️ champ côté back
  if (typeof description === "string") form.append("description", description);
  form.append("is_active", is_active ? 1 : 0);

  try {
    const { data } = await axios.post(
      `${galleriesBase}/${encodeURIComponent(gallerieId)}/photos?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // Laisse axios gérer le Content-Type multipart
        },
        onUploadProgress,
      }
    );
    return data;
  } catch (error) {
    console.error("[addAlbumPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Remplace le fichier d'une photo existante (route canonique avec gallerie_id).
 * - Route: PATCH /api/galleries/:gallerie_id/photos/:id/photo
 * - Champ fichier: "image"
 * - Query: ?format=webp|jpg|png (def: webp)
 * - Retour: { id, gallerie_id, photo_url }
 *
 * @param {number|string} gallerieId
 * @param {number|string} photoId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<object>}
 */
export const replaceAlbumPhoto = async (gallerieId, photoId, file, options = {}) => {
  if (!gallerieId) throw new Error("gallerieId requis");
  if (!photoId) throw new Error("photoId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${galleriesBase}/${encodeURIComponent(gallerieId)}/photos/${encodeURIComponent(photoId)}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
        },
        onUploadProgress,
      }
    );
    return data;
  } catch (error) {
    console.error("[replaceAlbumPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Remplace le fichier d'une photo existante (variante Fallback SANS gallerie_id).
 * - Route: PATCH /api/photos/:id/photo
 * - Champ fichier: "image"
 * - Query: ?format=webp|jpg|png (def: webp)
 * - Retour: { id, gallerie_id, photo_url }
 *
 * @param {number|string} photoId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<object>}
 */
export const replaceAlbumPhotoById = async (photoId, file, options = {}) => {
  if (!photoId) throw new Error("photoId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${photosBase}/${encodeURIComponent(photoId)}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
        },
        onUploadProgress,
      }
    );
    return data;
  } catch (error) {
    console.error("[replaceAlbumPhotoById] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Helper: renvoie une URL exploitable dans <img src=...>
 * - Si commence par /uploads/ => préfixe avec baseUrl (API)
 * - Sinon renvoie tel quel (ex: Cloudinary https://...)
 *
 * @param {string} photoUrl
 * @returns {string}
 */
export const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/uploads/")) {
    return `${baseUrl}${photoUrl}`;
  }
  return photoUrl;
};
